import Quill, {Delta} from 'quill';
import {match} from 'ts-pattern';

import {KeyboardEvent} from 'react';

import {isNil} from 'ramda';

import {RichTextTemplateTokenSuggestion} from '../types';
import {findTemplateTokenTrigger} from './findTemplateTokenTrigger';
import {TEMPLATE_TOKEN} from './TEMPLATE_TOKEN';

// An embed is one index position with no text. A non-space character keeps the text aligned with
// Quill's indexes and stops a token directly before `/` from counting as a word boundary.
const EMBED_CHARACTER = '\uFFFC';
const SPACE_AFTER_TOKEN_PATTERN = /^[ \u00A0\t]$/;
// The token and the space after it, whether inserted or already there.
const TOKEN_WITH_SPACE_LENGTH = 2;

export type TemplateTokenSuggestionMenuState = {
  items: RichTextTemplateTokenSuggestion[];
  activeIndex: number;
  anchor: {top: number; left: number};
};

type OpenTrigger = {
  index: number;
  caret: number;
  query: string;
  items: RichTextTemplateTokenSuggestion[];
  activeIndex: number;
};

type Options = {
  getItems: () => RichTextTemplateTokenSuggestion[];
  /** The positioned element the menu anchor is rendered in. */
  getAnchorParent: () => HTMLElement | null;
  onStateChange: (state: TemplateTokenSuggestionMenuState | null) => void;
};

const readLineTextBeforeCaret = (editor: Quill, caret: number) => {
  const [line, offset] = editor.getLine(caret);

  if (isNil(line)) {
    return null;
  }

  const lineStart = caret - offset;
  const text = editor
    .getContents(lineStart, offset)
    .ops.map((op) => (typeof op.insert === 'string' ? op.insert : EMBED_CHARACTER))
    .join('');

  return {lineStart, text};
};

/**
 * The `/` menu of one editor: it follows the caret, filters the items by the query typed after
 * `/` and replaces `/query` with a token. The editor's keys are taken only while the menu is open.
 */
export function createTemplateTokenSuggestionMenu(options: Options) {
  let editor: Quill | null = null;
  let open: OpenTrigger | null = null;
  // The trigger closed with Escape or an outside click stays closed until the caret leaves it.
  let dismissedIndex: number | null = null;

  const emit = () => {
    if (isNil(editor) || isNil(open)) {
      options.onStateChange(null);
      return;
    }

    const bounds = editor.getBounds(open.index);
    const parent = options.getAnchorParent();
    const containerRect = editor.container.getBoundingClientRect();
    const parentRect = parent?.getBoundingClientRect() ?? containerRect;

    options.onStateChange({
      items: open.items,
      activeIndex: open.activeIndex,
      anchor: {
        top: containerRect.top - parentRect.top + (bounds?.bottom ?? 0),
        left: containerRect.left - parentRect.left + (bounds?.left ?? 0),
      },
    });
  };

  const close = () => {
    if (!isNil(open)) {
      open = null;
      emit();
    }
  };

  const evaluate = () => {
    const range = editor?.getSelection();

    if (isNil(editor) || !editor.isEnabled() || !editor.hasFocus() || isNil(range)) {
      dismissedIndex = null;
      close();
      return;
    }

    const lineText = range.length === 0 ? readLineTextBeforeCaret(editor, range.index) : null;
    const trigger = isNil(lineText) ? null : findTemplateTokenTrigger(lineText.text);

    if (isNil(lineText) || isNil(trigger)) {
      dismissedIndex = null;
      close();
      return;
    }

    const index = lineText.lineStart + trigger.offset;

    if (index === dismissedIndex) {
      close();
      return;
    }

    dismissedIndex = null;
    const query = trigger.query.toLowerCase();
    const items = options.getItems().filter((item) => item.label.toLowerCase().includes(query));

    if (items.length === 0) {
      close();
      return;
    }

    const isSameQuery = open?.index === index && open.query === trigger.query;
    const activeIndex =
      isSameQuery && !isNil(open) ? Math.min(open.activeIndex, items.length - 1) : 0;

    open = {index, caret: range.index, query: trigger.query, items, activeIndex};
    emit();
  };

  const dismiss = () => {
    if (!isNil(open)) {
      dismissedIndex = open.index;
      close();
    }
  };

  const highlight = (activeIndex: number) => {
    if (!isNil(open) && activeIndex !== open.activeIndex) {
      open = {...open, activeIndex};
      emit();
    }
  };

  const moveHighlight = (step: number) => {
    if (!isNil(open)) {
      highlight((open.activeIndex + step + open.items.length) % open.items.length);
    }
  };

  const select = (activeIndex: number) => {
    const item = open?.items[activeIndex];

    if (isNil(editor) || isNil(open) || isNil(item)) {
      return;
    }

    const {index, caret} = open;
    const attributes = editor.getContents(index, 1).ops[0]?.attributes;
    const hasSpaceAfter = SPACE_AFTER_TOKEN_PATTERN.test(editor.getText(caret, 1));
    const change = new Delta()
      .retain(index)
      .delete(caret - index)
      .insert({[TEMPLATE_TOKEN.blotName]: {code: item.code, label: item.label}}, attributes);

    open = null;
    // Its own history step: one undo brings back the `/query` text.
    editor.history.cutoff();
    editor.updateContents(hasSpaceAfter ? change : change.insert(' ', attributes), 'user');
    editor.history.cutoff();
    editor.setSelection(index + TOKEN_WITH_SPACE_LENGTH, 'user');
    emit();
  };

  const selectActive = () => {
    if (!isNil(open)) {
      select(open.activeIndex);
    }
  };

  /**
   * Takes the menu keys pressed in the editor while the menu is open and stops them. Returns
   * whether it took the key.
   */
  const handleKeyDown = (event: KeyboardEvent) => {
    const hasModifier = event.shiftKey || event.altKey || event.ctrlKey || event.metaKey;
    const isInEditor = event.target instanceof Node && editor?.container.contains(event.target);

    if (isNil(open) || !isInEditor || event.nativeEvent.isComposing || hasModifier) {
      return false;
    }

    const action = match(event.key)
      .with('ArrowDown', () => () => moveHighlight(1))
      .with('ArrowUp', () => () => moveHighlight(-1))
      .with('Enter', 'Tab', () => selectActive)
      .with('Escape', () => dismiss)
      .otherwise(() => null);

    if (isNil(action)) {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();
    action();

    return true;
  };

  // The anchor is relative to the editor box, so it moves when the editor scrolls its text.
  const handleScroll = () => {
    if (!isNil(open)) {
      emit();
    }
  };

  const attach = (quill: Quill) => {
    editor = quill;
    const root = quill.root;

    quill.on('editor-change', evaluate);
    root.addEventListener('scroll', handleScroll);

    return () => {
      quill.off('editor-change', evaluate);
      root.removeEventListener('scroll', handleScroll);
      editor = null;
      open = null;
      dismissedIndex = null;
      options.onStateChange(null);
    };
  };

  return {attach, select, highlight, dismiss, handleKeyDown};
}
